'use client';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useAuthContext } from '@/components/AuthProvider';
import { useLanguage } from '@/components/LanguageProvider';

export default function PricingWithTransition({ onOpenAppModal }: { onOpenAppModal: () => void }) {
  const { regionData } = useAuthContext();
  const { t } = useLanguage();
  const tp = t.landing.pricing;
  
  let price = '121 ₽';
  if (regionData) {
      const country = regionData.countryCode;
      if (country === 'UA') price = '55 ₴';
      else if (['RU'].includes(country)) price = '121 ₽';
      else price = '€1.5';
  }

  return (
    <section id="access" className="relative py-20 md:py-32 w-full z-20 pointer-events-auto min-h-[85vh] flex flex-col justify-center scroll-mt-24 md:scroll-mt-32">
      <div className="absolute inset-0 bg-transparent z-0 pointer-events-none hidden md:block">
        <motion.div animate={{ x: [0, 50, 0], y: [0, -30, 0] }} transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }} className="absolute top-[20%] left-[10%] w-[300px] h-[300px] rounded-full bg-[radial-gradient(circle_at_center,_rgba(102,127,255,0.15)_0%,_transparent_70%)]" />
        <motion.div animate={{ x: [0, -50, 0], y: [0, 30, 0] }} transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }} className="absolute bottom-[20%] right-[10%] w-[300px] h-[300px] rounded-full bg-[radial-gradient(circle_at_center,_rgba(168,85,247,0.15)_0%,_transparent_70%)]" />
      </div>

      <div className="relative z-40 w-full max-w-5xl mx-auto px-4 flex flex-col justify-center items-center">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5 }} className="text-center w-full px-4 mb-10 md:mb-16">
          <h2 className="font-mc text-4xl md:text-7xl uppercase text-white mb-3 md:mb-4">{tp.title}</h2>
          <p className="text-sm md:text-xl text-[#aaaaaa] font-medium max-w-xl mx-auto">{tp.subtitle}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: 0.1 }} className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 w-full max-w-4xl mx-auto px-2 md:px-4 items-stretch">
           {/* Карточка 1: Заявка (Бесплатно) */}
           <PricingCard 
             title={tp.formTitle} 
             features={tp.formFeatures} 
             highlighted={false} 
             onClick={onOpenAppModal} 
             btnText={tp.formBtn}
             trackEvent="apply_button_clicked"
             trackLabel="application" 
           />
           {/* Карточка 2: Пропуск (Платно) */}
           <PricingCard 
             title={tp.passTitle} 
             price={price} 
             sub={tp.passSub} 
             features={tp.passFeatures} 
             highlighted={true} 
             onClick={() => window.location.href = "/buy/multiverse"} 
             btnText={tp.passBtn} 
             trackEvent="buy_pass_clicked"
             trackLabel="buy pass"
           />
        </motion.div>
      </div>
    </section>
  );
}

function PricingCard({ title, price, sub, features, highlighted, onClick, btnText, trackEvent, trackLabel }: any) {
  return (
    <div className={`relative rounded-[24px] w-full p-6 md:p-10 border outline-none flex flex-col h-full ${highlighted ? 'bg-[#1A1A20] border-[#667fff]/30 overflow-hidden' : 'bg-[#161616] border-white/5'}`}>
      {highlighted && <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-b from-[#667fff]/5 to-transparent pointer-events-none" />}
      <div className="relative z-10 flex justify-between items-start mb-6 md:mb-8 border-b border-white/10 pb-6 md:pb-8">
        <div>
          <h3 className="text-2xl md:text-3xl font-extrabold tracking-tighter uppercase text-white mb-1">{title}</h3>
          <p className="text-[#aaaaaa] text-xs md:text-sm font-medium">{sub}</p>
        </div>
        {price && <div className={`text-3xl md:text-4xl font-extrabold tracking-tighter ${highlighted ? 'text-[#7a93ff]' : 'text-white'}`}>{price}</div>}
      </div>
      <ul className="space-y-3 md:space-y-4 mb-8 md:mb-10 text-sm md:text-[15px] text-[#aaaaaa] relative z-10 font-medium flex-1">
        {features.map((f: string, i: number) => (
          <li key={i} className="flex gap-3 items-center"><Check size={20} strokeWidth={2.5} className={`${highlighted ? 'text-[#667fff]' : 'text-white/40'} shrink-0`} />{f}</li> 
        ))}
      </ul>
      <motion.button 
        initial="initial" 
        whileHover="hover" 
        onClick={onClick} 
        data-track={trackEvent}
        data-track-label={trackLabel}
        className={`mt-auto group relative z-10 w-full py-3.5 md:py-4 rounded-[12px] text-sm md:text-base uppercase transition-all duration-300 outline-none cursor-pointer ${highlighted ? 'bg-[#667fff] text-white hover:bg-[#526beb]' : 'bg-[#283059] text-white hover:bg-[#343e73]'}`}
      >
        <span className="font-mc relative z-10 transition-all pointer-events-none">{btnText}</span>
      </motion.button>
    </div>
  );
}